import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Botao } from '../../../components/Botao';
import { CorpoDaPagina } from '../../../components/CorpoDaPagina';
import { Icone } from '../../../components/Icone';
import { Input } from '../../../components/Input';
import { Seletor } from '../../../components/Seletor';
import { validarCPF } from '../../../functions/validateCPF';
import { InstituicaoStore } from '../../../stores/InstituicaoStore';
import { MunicipioStore } from '../../../stores/MunicipioStore';
import { UsuarioInstituicaoStore } from '../../../stores/UsuarioInstituicaoStore';
import {
  CampoConteiner,
  Formulario,
  TabelaVinculos,
  Td,
  THeader,
  Tr,
  AcoesConteiner,
} from './styles';

function UsuarioFormulario() {
  const [dependencia, atribuirDependencia] = useState('');
  const [instituicoes, atribuirInstituicoes] = useState([]);
  const [municipios, atribuirMunicipios] = useState([]);
  const [municipioNome, atribuirMunicipioNome] = useState('');
  const [vinculos, atribuirVinculos] = useState([]);

  const formulario = useRef(null);

  let history = useHistory();

  const adicionarVinculo = useCallback(
    (data) => {
      formulario.current?.setErrors({});
      let error = false;
      let instituicao = {};

      if (data.cpf === '' || data.cpf.length !== 14) {
        formulario.current?.setFieldError('cpf', 'Campo obrigatório');
        error = true;
      } else if (!validarCPF(data.cpf)) {
        toast.warning('CPF inválido!');
        error = true;
      }

      if (data.instituicaoDeEnsinoId === '') {
        formulario.current?.setFieldError('instituicaoDeEnsinoId', 'Campo obrigatório');
        error = true;
      } else {
        instituicao = instituicoes.find(
          (instituicaoUsuario) =>
            Number(instituicaoUsuario.value) === Number(data.instituicaoDeEnsinoId),
        );
        data.nomeInstituicao = instituicao.label;
        data.instituicaoDeEnsinoId = instituicao.value;
      }

      if (error) {
        return;
      }

      if (
        vinculos.some(
          (vinculoUsuario) =>
            vinculoUsuario.cpf === data.cpf &&
            vinculoUsuario.instituicaoDeEnsinoId === data.instituicaoDeEnsinoId,
        )
      ) {
        toast.warning('CPF com instituição de ensino já informado!');
        return;
      }

      atribuirVinculos((array) => [...array, { ...data }]);
    },
    [instituicoes, vinculos],
  );

  const enviarFormulario = useCallback(async () => {
    if (vinculos.length === 0) {
      toast.warning('Necessário pelo menos um vinculo!');
      return;
    }
    const novoVinculo = [...vinculos];
    let data = {};
    const instituicaoDeEnsinoId = formulario.current.getFieldValue('instituicaoDeEnsinoId');
    data.cpf = formulario.current.getFieldValue('cpf');
    if (instituicaoDeEnsinoId !== '' && validarCPF(data.cpf)) {
      const instituicao = instituicoes.find(
        (i) => Number(i.value) === Number(instituicaoDeEnsinoId),
      );
      if (instituicao) {
        data.nomeInstituicao = instituicao.label;
        data.instituicaoDeEnsinoId = instituicao.value;
        if (
          !vinculos.some(
            (v) => v.cpf === data.cpf && v.instituicaoDeEnsinoId === data.instituicaoDeEnsinoId,
          )
        ) {
          novoVinculo.push(data);
        }
      }
    }
    await UsuarioInstituicaoStore.criar(novoVinculo).then((response) => {
      if (response.status === 201) {
        toast.success(response.data.mensagem);
        history.push('/usuario');
      } else if (response.status >= 400 && response.status < 500) {
        toast.error(response.data.mensagem);
        toast.warning('Um ou mais vínculos não puderam ser criado(s). Verifique acima quais são.');
      } else {
        toast.error(response.statusText);
      }
    });
  }, [vinculos, history, instituicoes]);

  const obterInstituicoes = useCallback(async (dependencia, municipio) => {
    if (dependencia === '' || municipio === '') {
      atribuirInstituicoes([]);
      return;
    }

    await InstituicaoStore.obterInstituicoesFiltradas({
      dependenciaAdministrativa: dependencia,
      municipio,
    }).then((response) => {
      if (response.status === 200) {
        atribuirInstituicoes([
          { label: 'Selecione', value: '' },
          ...response.data.map((item) => ({
            label: item.nome,
            value: item.id,
          })),
        ]);
      } else if (response.status === 204) {
        toast.error('não há instituições cadastradas para a dependência administrativa informada');
      } else toast.error(response.data.mensagem);
    });
  }, []);

  const removerArquivo = useCallback((index) => {
    atribuirVinculos((array) =>
      array.filter((vinculoUsuario, indiceVinculo) => indiceVinculo !== index),
    );
  }, []);

  const mudarDependencia = (value) => {
    atribuirDependencia(value);
    obterInstituicoes(value, municipioNome);
  };

  const mudarMunicipio = (value) => {
    atribuirMunicipioNome(value);
    obterInstituicoes(dependencia, value);
  };

  useEffect(() => {
    const call = async () => {
      await MunicipioStore.obterMunicipios({}).then((response) => {
        if (response.status === 200) {
          atribuirMunicipios([
            { label: 'Selecione', value: '' },
            ...response.data.map((item) => ({
              label: item.nome,
              value: item.nome,
            })),
          ]);
        } else if (response.status === 204) {
          toast.error('não há municípios cadastrados');
        } else toast.error(response.data.mensagem);
      });
    };

    call();
  }, []);

  return (
    <CorpoDaPagina voltar titulo='Vínculo de usuário'>
      <Formulario ref={formulario} onSubmit={adicionarVinculo}>
        <CampoConteiner>
          <Input nome='cpf' titulo='CPF' mascara placeholder={'Informe o CPF'} />

          <Seletor
            nome='municipio'
            titulo='Município'
            aoAtribuirValor={mudarMunicipio}
            opcoes={municipios}
            desabilitado={municipios.length === 0}
          />
          <Seletor
            nome='dependenciaAdministrativa'
            titulo='Dependência administrativa'
            aoAtribuirValor={mudarDependencia}
            opcoes={[
              { label: 'Selecione', value: '' },
              { label: 'Federal', value: 'Federal' },
              { label: 'Privada', value: 'Privada' },
              { label: 'Municipal', value: 'Municipal' },
              { label: 'Estadual', value: 'Estadual' },
            ]}
          />
        </CampoConteiner>
        <CampoConteiner>
          <Seletor
            nome='instituicaoDeEnsinoId'
            titulo='Instituição de ensino'
            opcoes={instituicoes}
            desabilitado={instituicoes.length === 0}
          />
          <Botao tipo='submit' cor='success' texto={'Adicionar'} />
        </CampoConteiner>
      </Formulario>
      {vinculos.length > 0 && (
        <>
          <TabelaVinculos>
            <thead>
              <Tr>
                <THeader>CPF</THeader>
                <THeader>Nome da Instituição</THeader>
                <THeader></THeader>
              </Tr>
            </thead>
            <tbody>
              {vinculos.map((vinculo, indiceVinculo) => (
                <Tr key={`tr${indiceVinculo}`}>
                  <Td>{vinculo.cpf}</Td>
                  <Td>
                    {vinculo.nomeInstituicao} ({vinculo.municipio})
                  </Td>
                  <Td>
                    <AcoesConteiner>
                      <Botao aoClicar={() => removerArquivo(indiceVinculo)} cor='DANGER' outlined>
                        <Icone nome='FaTrash' />
                      </Botao>
                    </AcoesConteiner>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TabelaVinculos>
          <AcoesConteiner>
            <Botao texto='Salvar' aoClicar={() => enviarFormulario()} />
          </AcoesConteiner>
        </>
      )}
    </CorpoDaPagina>
  );
}

export default UsuarioFormulario;
